"use client"

import { keyframes } from '@mui/system';
import { MouseEvent, ReactNode, useEffect, useRef } from 'react';

const recentLinkPulse = keyframes`
  0%, 100% { opacity: 0; }
  16%, 82% { opacity: 0.42; }
`;

const recentInlineLinkPulse = keyframes`
  0%, 100% { background-color: rgba(33, 150, 243, 0); }
  16%, 82% { background-color: rgba(33, 150, 243, 0.22); }
`;

export const RECENT_LINK_STORAGE_PREFIX = 'recent-link';

// How long to keep waiting for the clicked link to come back. The list it lives
// in can still be fetching, so give it room; this is only a guard so a link that
// never returns does not leave an observer running.
const RESTORE_TIMEOUT_MS = 8000;

type RecentLinkSnapshot = {
    href?: string;
    index?: number;
    viewportTop?: number;
    // Whether the scope that wrote this one puts the offset back too, rather than
    // only pulsing the card. ScrollRestorer reads it to decide whether it can let
    // this restore do the job for it.
    restoresScroll?: boolean;
};

export const recentLinkHighlighterSx = {
    '& .recent-link-highlight-card': {
        position: 'relative',
        zIndex: 1,
        '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            backgroundColor: 'rgba(33, 150, 243, 0.55)',
            pointerEvents: 'none',
            animation: `${recentLinkPulse} 1s ease-out`,
        },
    },
    '& .recent-link-highlight-inline': {
        borderRadius: '4px',
        animation: `${recentInlineLinkPulse} 1s ease-out`,
    },
};

export default function RecentLinkHighlighter({ children, scope, allowBlankTarget = false, restoreScrollPosition = false, linkSelector = 'a[href]' }: {
    scope: string;
    allowBlankTarget?: boolean;
    restoreScrollPosition?: boolean;
    linkSelector?: string;
    children: (props: {
        ref: React.Ref<HTMLElement>;
        onClickCapture: (event: MouseEvent<HTMLElement>) => void;
    }) => ReactNode;
}) {
    const containerRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const stopWatching = new Set<() => void>();

        const restore = () => {
            // A fresh restore takes over from any watcher an earlier one left behind.
            Array.from(stopWatching).forEach(stop => stop());

            const storageKey = getRecentLinkStorageKey(scope);
            const snapshot = readRecentLinkSnapshot(storageKey);
            if (!storageKey || !snapshot) return;

            const tryRestore = () => {
                if (!restoreRecentLink(containerRef.current, snapshot, restoreScrollPosition, linkSelector)) return false;
                sessionStorage.removeItem(storageKey);
                return true;
            };

            // With the list already whole this is the only attempt needed.
            if (tryRestore()) return;

            const container = containerRef.current;
            if (!container) return;

            // The rest of the list can still be on its way, so wait on rows being
            // added rather than polling blind.
            const stop = () => {
                observer.disconnect();
                window.clearTimeout(timer);
                stopWatching.delete(stop);
            };
            const observer = new MutationObserver(() => {
                if (tryRestore()) stop();
            });
            const timer = window.setTimeout(stop, RESTORE_TIMEOUT_MS);

            observer.observe(container, { childList: true, subtree: true });
            stopWatching.add(stop);
        };

        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) restore();
        };

        const handlePopState = () => {
            restore();
        };

        restore();
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('popstate', handlePopState);
            Array.from(stopWatching).forEach(stop => stop());
        };
    }, [linkSelector, restoreScrollPosition, scope]);

    const handleClickCapture = (event: MouseEvent<HTMLElement>) => {
        storeRecentLink(event, containerRef.current, scope, allowBlankTarget, linkSelector, restoreScrollPosition);
    };

    return children({
        ref: containerRef,
        onClickCapture: handleClickCapture,
    });
}

function getRecentLinkStorageKey(scope: string) {
    if (typeof window === 'undefined') return null;
    return `${RECENT_LINK_STORAGE_PREFIX}:${scope}:${window.location.pathname}${window.location.search}`;
}

function getAnchorHref(anchor: HTMLAnchorElement) {
    return anchor.getAttribute('href') ?? anchor.href;
}

function getMatchingAnchorIndex(container: HTMLElement, anchor: HTMLAnchorElement, linkSelector: string) {
    const href = getAnchorHref(anchor);
    return Array.from(container.querySelectorAll<HTMLAnchorElement>(linkSelector))
        .filter(item => getAnchorHref(item) === href)
        .indexOf(anchor);
}

function storeRecentLink(event: MouseEvent<HTMLElement>, container: HTMLElement | null, scope: string, allowBlankTarget: boolean, linkSelector: string, restoreScrollPosition: boolean) {
    if (!container) return;
    if (!isPlainLeftClick(event)) return;

    const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
    if (!anchor || !anchor.matches(linkSelector) || !container.contains(anchor) || !shouldStoreAnchor(anchor, allowBlankTarget)) return;

    const storageKey = getRecentLinkStorageKey(scope);
    if (!storageKey) return;

    try {
        sessionStorage.setItem(storageKey, JSON.stringify({
            href: getAnchorHref(anchor),
            index: getMatchingAnchorIndex(container, anchor, linkSelector),
            viewportTop: anchor.getBoundingClientRect().top,
            restoresScroll: restoreScrollPosition,
        }));
    } catch {
        // sessionStorage can be unavailable in private or constrained contexts.
    }
}

function isPlainLeftClick(event: MouseEvent<HTMLElement>) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function shouldStoreAnchor(anchor: HTMLAnchorElement, allowBlankTarget: boolean) {
    if (anchor.hasAttribute('download')) return false;

    try {
        const url = new URL(anchor.href);
        if (anchor.target === '_blank' && (!allowBlankTarget || !isInternalAppUrl(url))) return false;

        const isSamePageHash = isInternalAppUrl(url)
            && url.pathname === window.location.pathname
            && url.search === window.location.search
            && !!url.hash;

        return !isSamePageHash && url.protocol !== 'javascript:';
    } catch {
        return false;
    }
}

export function isInternalAppUrl(url: URL) {
    return getInternalHostnames().has(url.hostname.replace(/^www\./, ''));
}

function getInternalHostnames() {
    const hostnames = new Set<string>(['vahdatoptic.com']);

    if (typeof window !== 'undefined') {
        hostnames.add(window.location.hostname);
    }

    [
        process.env.NEXT_PUBLIC_HOME_URL,
        process.env.NEXT_PUBLIC_APP_URL,
    ].forEach((baseUrl) => {
        if (!baseUrl) return;

        try {
            hostnames.add(new URL(baseUrl).hostname);
        } catch {
            // Ignore invalid environment URL values.
        }
    });

    return new Set(Array.from(hostnames).map(hostname => hostname.replace(/^www\./, '')));
}

// Whether a list on the address being returned to has a click of its own to put
// back and restores the scroll position while doing it.
//
// A scope that only highlights the card answers no. Counting it was what left a
// landing page at the top: the offset saver stood down for a restore that pulsed
// the card and never scrolled, so nothing put the page back at all.
export function recentLinkWillRestoreScroll() {
    try {
        const address = `:${window.location.pathname}${window.location.search}`;
        for (let index = 0; index < sessionStorage.length; index++) {
            const key = sessionStorage.key(index);
            if (!key?.startsWith(`${RECENT_LINK_STORAGE_PREFIX}:`) || !key.endsWith(address)) continue;
            if (readRecentLinkSnapshot(key)?.restoresScroll) return true;
        }
        return false;
    } catch {
        return false;
    }
}

function readRecentLinkSnapshot(storageKey: string | null): RecentLinkSnapshot | null {
    if (!storageKey) return null;

    try {
        const rawValue = sessionStorage.getItem(storageKey);
        if (!rawValue) return null;
        return JSON.parse(rawValue) as RecentLinkSnapshot;
    } catch {
        sessionStorage.removeItem(storageKey);
        return null;
    }
}

function restoreRecentLink(container: HTMLElement | null, recentLink: RecentLinkSnapshot, restoreScrollPosition: boolean, linkSelector: string) {
    if (!container || !recentLink.href) return false;

    try {
        const links = Array.from(container.querySelectorAll<HTMLAnchorElement>(linkSelector))
            .filter(item => getAnchorHref(item) === recentLink.href);
        const link = links[Math.max(recentLink.index ?? 0, 0)] ?? links[0];
        if (!link || link.getClientRects().length === 0) return false;

        if (restoreScrollPosition && typeof recentLink.viewportTop === 'number') {
            // Once straight away so the card is already in place at the first paint,
            // then again after layout settles to absorb the shift from images and
            // fonts landing.
            const applyScroll = () => {
                const offset = link.getBoundingClientRect().top - recentLink.viewportTop!;
                if (Math.abs(offset) > 1) window.scrollBy(0, offset);
            };

            applyScroll();
            window.requestAnimationFrame(() => window.requestAnimationFrame(applyScroll));
        }

        const highlightTarget = getHighlightTarget(link);
        const highlightClass = getHighlightClass(highlightTarget);
        highlightTarget.classList.add(highlightClass);
        window.setTimeout(() => {
            highlightTarget.classList.remove(highlightClass);
        }, 1000);
        return true;
    } catch {
        return false;
    }
}

function getHighlightTarget(link: HTMLAnchorElement) {
    const children = Array.from(link.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
    if (children.length !== 1) return link;

    const linkRect = link.getBoundingClientRect();
    const childRect = children[0].getBoundingClientRect();
    const isSameBox = childRect.width >= linkRect.width * 0.9 && childRect.height >= linkRect.height * 0.9;

    return isSameBox ? children[0] : link;
}

function getHighlightClass(element: HTMLElement) {
    const rect = element.getBoundingClientRect();
    const isInline = rect.height < 48 || rect.width < 160;
    return isInline ? 'recent-link-highlight-inline' : 'recent-link-highlight-card';
}
